import React, { useState, useEffect } from 'react';
import { Avatar } from 'antd';
import { useTranslation } from 'react-i18next';
import { Team } from 'models/CommonType';
import { getTeamMemberNames, formatMilesText } from 'services/TeamService';
import './TeamRow.css';

interface TeamRowProps {
  team: Team;
  hasTeam: boolean;
  shouldShowMiles?: boolean;
}

const TeamRow: React.FC<TeamRowProps> = ({
  team,
  hasTeam,
  shouldShowMiles = true
}) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getStatusBadge = () => {
    if (!shouldShowMiles) return null;

    // Hide "Your Team" badge on mobile to avoid duplication
    if (team.code && !isMobile) {
      return (
        <div className="status-badge your-team">
          {t('#Teams.your_team')}
        </div>
      );
    } else if (team.isFull && !hasTeam) {
      return (
        <div className="status-badge team-full">
          {t('#Teams.team_full')}
        </div>
      );
    } else if (!hasTeam) {
      return (
        <div className="status-badge join-team">
          {t('#Teams.join_team')}
        </div>
      );
    }
    return null;
  };

  const getMemberAvatars = () => {
    const maxAvatars = 4;
    const membersToShow = team.members.slice(0, maxAvatars);

    return (
      <div className="team-avatars">
        {membersToShow.map((member, index) => (
          <Avatar
            key={member.id}
            size={32}
            src={member.profile?.image_small_url}
            className="team-avatar"
            style={{
              zIndex: maxAvatars - index,
              marginLeft: index > 0 ? '-8px' : 0
            }}
          >
            {member.first_name?.[0]}{member.last_name?.[0]}
          </Avatar>
        ))}
        {team.members.length > maxAvatars && (
          <div className="avatar-count" style={{ zIndex: 0, marginLeft: '-8px' }}>
            +{team.members.length - maxAvatars}
          </div>
        )}
      </div>
    );
  };

  const memberNames = getTeamMemberNames(team.members);

  return (
    <div className="team-row">
      <div className="team-content">
        <div className="team-header">
          <div className="team-avatars-container">
            {getMemberAvatars()}
          </div>
          {getStatusBadge()}
        </div>

        <div className="team-info">
          {isMobile ? (
            // Mobile compact layout
            <>
              <h3 className="team-name">{team.name}</h3>
              {memberNames && (
                <div className="team-members-text">
                  {t('#Teams.team_member_text', { memberNames })}
                </div>
              )}
              <div className="team-stats-mobile">
                <span className="team-members-count">
                  {team.members.length} {team.members.length === 1 ? 'member' : 'members'}
                </span>
                <span className="team-separator">|</span>
                <span className="team-miles-mobile">{team.miles} miles</span>
              </div>
            </>
          ) : (
            // Desktop layout
            <>
              <h3 className="team-name">{team.name}</h3>
              <div className="team-details">
                <div className="team-members">
                  {memberNames && (
                    <span className="team-members-text">
                      {t('#Teams.team_member_text', { memberNames })}
                    </span>
                  )}
                </div>

                {shouldShowMiles && (
                  <div className="team-miles">
                    {formatMilesText(team.miles)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamRow;